import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import ImageTag from "../components/reuseable/imageTag";
import { ChevronLeft } from "lucide-react";
import {
  RequestPasswordResetOtpAction,
  clearPasswordOtpState,
  clearPendingEmailForPasswordOtp,
} from "../Store/Actions/AuthActions";
import { PasswordOtpActionConst } from "../Store/Constants/AuthConstants";

function ForgetPassword() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    passwordOtpRequestLoading,
    passwordOtpRequestError,
    passwordOtpRequestSuccess,
    pendingEmailForPasswordOtp,
  } = useSelector((state) => state.auth);

  const [email, setEmail] = useState("");
  const [localError, setLocalError] = useState("");

  // Clear all password OTP-related states when component mounts
  // This ensures clean state when navigating to forget password page (handles browser back button and re-navigation)
  useEffect(() => {
    // Clear all password OTP states (success, error, loading, pending email, pending OTP)
    dispatch(clearPasswordOtpState());
    dispatch(clearPendingEmailForPasswordOtp());
    // Clear passwordOtpRequestSuccess to prevent stale state from triggering navigation on mount
    dispatch({
      type: PasswordOtpActionConst.REQUEST_CODE_FAIL,
      result: null,
    });
  }, [dispatch]);

  useEffect(() => {
    if (passwordOtpRequestError) {
      const message = Array.isArray(passwordOtpRequestError.message)
        ? passwordOtpRequestError.message.join(", ")
        : passwordOtpRequestError.message || "Unable to send OTP. Try again.";
      toast.error(message);
    }
  }, [passwordOtpRequestError]);

  // Handle password OTP request success
  useEffect(() => {
    if (passwordOtpRequestSuccess && pendingEmailForPasswordOtp) {
      const message =
        passwordOtpRequestSuccess?.message ||
        passwordOtpRequestSuccess?.data?.message ||
        "If the email exists, a password reset code has been sent.";
      toast.success(message);
      navigate("/otp-password-reset");
      // Clear passwordOtpRequestSuccess after using it to prevent it from triggering again on remount
      dispatch({
        type: PasswordOtpActionConst.REQUEST_CODE_FAIL,
        result: null,
      });
    }
  }, [passwordOtpRequestSuccess, pendingEmailForPasswordOtp, navigate, dispatch]);

  const validateEmail = (value) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      setLocalError("Email is required.");
      return;
    }

    if (!validateEmail(trimmedEmail)) {
      setLocalError("Please enter a valid email address.");
      return;
    }

    setLocalError("");
    dispatch(
      RequestPasswordResetOtpAction({
        email: trimmedEmail,
      })
    );
  };

  return (
    <div className="lg:h-screen flex items-center">
      <div className="flex w-full lg:h-screen rounded-xl">
        <div className="w-[60%] h-full hidden lg:flex lg:flex-col items-center justify-center bg-gradient-to-b from-[#3FC4E3] to-[#006077]">
          <ImageTag
            path="/assets/images/auth/thumbnail.png"
            classes="size-[460px]"
            altText="login"
          />
          <p className="font-popinsBold text-white text-4xl mt-8 text-center">
            One Platform.
          </p>
          <p className="font-popinsBold text-white text-4xl mt-2 text-center">
            Endless Community Possibilities.
          </p>
        </div>
        <div className=" w-full lg:w-[40%] h-screen lg:h-full my-auto flex flex-col items-center justify-center bg-bg-auth bg-no-repeat bg-center bg-cover">
          <div className="w-[90%] lg:w-[75%] mx-auto flex flex-col items-center justify-center">
            <div className="w-full pt-8 h-full flex flex-col justify-center text-center">
              <Link to={"/"}>
                <div className="flex text-black/50 items-center gap-x-1 lg:ml-[-50px] font-popinsMedium">
                  <ChevronLeft size={18} />
                  <p>Back</p>
                </div>
              </Link>
              <h1 className="font-popinsBold  text-2xl 2xl:text-4xl mt-24">
                Forgot Password
              </h1>
              <p className="text-textColor mb-4 w-[90%] mx-auto mt-2">
                Enter your email address to receive a one-time password (OTP)
                that lets you reset your account password securely.
              </p>
            </div>
            <form className="w-full mt-8" onSubmit={handleSubmit}>
              <div className="relative w-full">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (localError) setLocalError("");
                  }}
                  placeholder="Email"
                  className={`peer w-full font-popinsMedium rounded-md border-2 bg-white px-4 py-2 text-black/50 outline-none transition-all duration-300 ease-in-out ${
                    localError
                      ? "border-red-500 focus:border-red-500 focus:shadow-[0_0_3px_rgba(239,68,68,0.5)]"
                      : "border-border focus:border-[#3FC4E3] focus:shadow-[0_0_3px_#3FC4E3]"
                  }`}
                />
              </div>
              {localError && (
                <p className="text-left text-sm text-red-500 mt-2 font-popinsMedium">
                  {localError}
                </p>
              )}
              <button
                type="submit"
                disabled={passwordOtpRequestLoading}
                className="relative overflow-hidden py-3 bg-themeColor text-white font-popinsSemiBold text-lg mt-8 rounded-md shadow-lg
           transform transition duration-300 ease-in-out
           hover:bg-[#3FC4E3] hover:scale-105 hover:shadow-[0_0_20px_rgba(63,196,227,0.6)] 
           active:scale-95 active:shadow-inner focus:outline-none w-full
           disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                <span className="flex items-center justify-center">
                  {passwordOtpRequestLoading ? "Sending..." : "Send OTP"}
                </span>
                <span className="absolute inset-0 rounded-full bg-white opacity-0 active:opacity-10 transition-opacity duration-300"></span>
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ForgetPassword;
